import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';

/**
 * Hook for fetching and managing GitHub data (commits, PRs) for a task
 */
export function useTaskGitHub(taskId, taskKey, organizationSlug) {
    const { axiosInstance } = useAuth();
    const [commits, setCommits] = useState([]);
    const [pullRequests, setPullRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch GitHub data for the task
    const fetchGitHubData = useCallback(async (showLoading = true) => {
        if (!taskKey || !organizationSlug) return;

        if (showLoading) {
            setIsLoading(true);
        }
        setError(null);

        try {
            const response = await axiosInstance.get(
                `/integrations/github/${organizationSlug}/tasks/${taskKey}`
            );

            setCommits(response.data.commits || []);
            setPullRequests(response.data.pullRequests || []);
        } catch (err) {
            // If GitHub integration is not configured, don't show error
            if (err.response?.status === 404) {
                setCommits([]);
                setPullRequests([]);
            } else {
                setError(err.response?.data?.message || 'Failed to fetch GitHub data');
            }
        } finally {
            if (showLoading) {
                setIsLoading(false);
            }
        }
    }, [taskKey, organizationSlug, axiosInstance]);

    // Link a commit or PR manually
    const linkItem = useCallback(async (type, id, repositoryFullName) => {
        if (!taskKey || !organizationSlug) return;

        try {
            await axiosInstance.post(
                `/integrations/github/${organizationSlug}/tasks/${taskKey}/link`,
                { type, id, repositoryFullName }
            );

            // Refresh data after linking (silent)
            await fetchGitHubData(false);
            return { success: true };
        } catch (err) {
            return {
                success: false,
                error: err.response?.data?.message || 'Failed to link item'
            };
        }
    }, [taskKey, organizationSlug, axiosInstance, fetchGitHubData]);

    // Unlink a commit or PR
    const unlinkItem = useCallback(async (type, id, repositoryFullName) => {
        if (!taskKey || !organizationSlug) return;

        // Optimistically remove from UI
        const oldCommits = commits;
        const oldPullRequests = pullRequests;

        if (type === 'commit') {
            setCommits(prev => prev.filter(c => c.sha !== id));
        } else {
            setPullRequests(prev => prev.filter(pr => !(pr.number === id && pr.repositoryFullName === repositoryFullName)));
        }

        try {
            await axiosInstance.delete(
                `/integrations/github/${organizationSlug}/tasks/${taskKey}/link`,
                { data: { type, id, repositoryFullName } }
            );

            return { success: true };
        } catch (err) {
            // Rollback on error
            setCommits(oldCommits);
            setPullRequests(oldPullRequests);
            return {
                success: false,
                error: err.response?.data?.message || 'Failed to unlink item'
            };
        }
    }, [taskKey, organizationSlug, axiosInstance, commits, pullRequests]);

    // Fetch data on mount and when dependencies change
    useEffect(() => {
        fetchGitHubData();
    }, [fetchGitHubData]);

    return {
        commits,
        pullRequests,
        isLoading,
        error,
        refetch: () => fetchGitHubData(false),
        linkCommit: (sha, repositoryFullName) => linkItem('commit', sha, repositoryFullName),
        linkPR: (number, repositoryFullName) => linkItem('pull_request', number, repositoryFullName),
        unlinkCommit: (sha, repositoryFullName) => unlinkItem('commit', sha, repositoryFullName),
        unlinkPR: (number, repositoryFullName) => unlinkItem('pull_request', number, repositoryFullName)
    };
}

/**
 * Hook for searching GitHub commits and PRs (for manual linking)
 */
export function useGitHubSearch(organizationSlug, taskKey = null) {
    const { axiosInstance } = useAuth();
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);

    const searchCommits = useCallback(async (query, repo = null) => {
        if (!organizationSlug) return { results: [], error: null };

        setIsSearching(true);
        setSearchError(null);
        try {
            const params = new URLSearchParams();
            if (query) params.append('q', query);
            if (repo) params.append('repo', repo);
            if (taskKey) params.append('taskKey', taskKey);

            const response = await axiosInstance.get(
                `/integrations/github/${organizationSlug}/commits/search?${params}`
            );
            return { results: response.data.commits || [], error: null };
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to search commits';
            console.error('Error searching commits:', errorMessage);
            setSearchError(errorMessage);
            return { results: [], error: errorMessage };
        } finally {
            setIsSearching(false);
        }
    }, [organizationSlug, taskKey, axiosInstance]);

    const searchPullRequests = useCallback(async (query, repo = null, state = null) => {
        if (!organizationSlug) return { results: [], error: null };

        setIsSearching(true);
        setSearchError(null);
        try {
            const params = new URLSearchParams();
            if (query) params.append('q', query);
            if (repo) params.append('repo', repo);
            if (state) params.append('state', state);
            if (taskKey) params.append('taskKey', taskKey);

            const response = await axiosInstance.get(
                `/integrations/github/${organizationSlug}/pulls/search?${params}`
            );
            return { results: response.data.pullRequests || [], error: null };
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to search pull requests';
            console.error('Error searching PRs:', errorMessage);
            setSearchError(errorMessage);
            return { results: [], error: errorMessage };
        } finally {
            setIsSearching(false);
        }
    }, [organizationSlug, taskKey, axiosInstance]);

    return {
        searchCommits,
        searchPullRequests,
        isSearching,
        searchError
    };
}

/**
 * Hook for managing GitHub integration settings
 */
export function useGitHubIntegration(organizationSlug) {
    const { axiosInstance } = useAuth();
    const [status, setStatus] = useState(null);
    const [repositories, setRepositories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch integration status
    const fetchStatus = useCallback(async () => {
        if (!organizationSlug) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.get(
                `/integrations/github/${organizationSlug}/status`
            );
            setStatus(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch status');
        } finally {
            setIsLoading(false);
        }
    }, [organizationSlug, axiosInstance]);

    // Fetch available repositories
    const fetchRepositories = useCallback(async () => {
        if (!organizationSlug) return;

        try {
            const response = await axiosInstance.get(
                `/integrations/github/${organizationSlug}/repositories`
            );
            setRepositories(response.data.repositories || []);
        } catch (err) {
            console.error('Error fetching repositories:', err);
        }
    }, [organizationSlug, axiosInstance]);

    // Get installation URL
    const getInstallUrl = useCallback(async () => {
        if (!organizationSlug) return null;

        try {
            const response = await axiosInstance.get(
                `/integrations/github/${organizationSlug}/install`
            );
            return response.data.installUrl;
        } catch (err) {
            console.error('Error getting install URL:', err);
            return null;
        }
    }, [organizationSlug, axiosInstance]);

    // Add repository
    const addRepository = useCallback(async (repoId, fullName, defaultBranch) => {
        if (!organizationSlug) return;

        try {
            await axiosInstance.post(
                `/integrations/github/${organizationSlug}/repositories`,
                { repoId, fullName, defaultBranch }
            );
            await fetchRepositories();
            return { success: true };
        } catch (err) {
            return {
                success: false,
                error: err.response?.data?.message || 'Failed to add repository'
            };
        }
    }, [organizationSlug, axiosInstance, fetchRepositories]);

    // Remove repository
    const removeRepository = useCallback(async (repoId) => {
        if (!organizationSlug) return;

        try {
            await axiosInstance.delete(
                `/integrations/github/${organizationSlug}/repositories/${repoId}`
            );
            await fetchRepositories();
            return { success: true };
        } catch (err) {
            return {
                success: false,
                error: err.response?.data?.message || 'Failed to remove repository'
            };
        }
    }, [organizationSlug, axiosInstance, fetchRepositories]);

    // Uninstall integration
    const uninstall = useCallback(async () => {
        if (!organizationSlug) return;

        try {
            await axiosInstance.delete(`/integrations/github/${organizationSlug}/uninstall`);
            setStatus({ installed: false });
            setRepositories([]);
            return { success: true };
        } catch (err) {
            return {
                success: false,
                error: err.response?.data?.message || 'Failed to uninstall'
            };
        }
    }, [organizationSlug, axiosInstance]);

    // Fetch on mount
    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    return {
        status,
        repositories,
        isLoading,
        error,
        refetch: fetchStatus,
        fetchRepositories,
        getInstallUrl,
        addRepository,
        removeRepository,
        uninstall
    };
}
